import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useDeviceStore } from '../store/device';
import { api, ApiError } from '../api/client';
import { useAuth } from '../store/auth';
import { displayName } from '../utils/display';

/**
 * Call controls pane (FR-39..45). Visible only when an active call is in
 * progress. Renders inline as a card on the Dashboard.
 */
export function CallControlsPane() {
  const activeCall = useDeviceStore(s => s.activeCall);
  const startedAt = useDeviceStore(s => s.callStartedAt);
  const setActive = useDeviceStore(s => s.setActive);
  const device = useDeviceStore(s => s.device);

  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [inputDeviceId, setInputDeviceId] = useState<string | undefined>();
  const [outputDeviceId, setOutputDeviceId] = useState<string | undefined>();
  const [showTransfer, setShowTransfer] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>();

  // Duration timer (FR-39).
  useEffect(() => {
    if (!activeCall || !startedAt) return;
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [activeCall, startedAt]);

  // Reset state when a new call starts.
  useEffect(() => {
    if (activeCall) {
      setMuted(false);
      setVolume(1);
      setShowTransfer(false);
      setActionError(undefined);
      // Initialize device-id selections from current selections.
      if (device?.audio) {
        const inSel = device.audio.inputDevice;
        if (inSel) setInputDeviceId(inSel.deviceId);
        const outSet = Array.from(device.audio.speakerDevices.get())[0];
        if (outSet) setOutputDeviceId(outSet.deviceId);
      }
    }
  }, [activeCall, device]);

  // Apply volume to Twilio's internal audio elements (D-028).
  useEffect(() => {
    if (!activeCall) return;
    const audios = document.querySelectorAll('audio');
    audios.forEach(a => { a.volume = volume; });
  }, [volume, activeCall]);

  if (!activeCall || !device) return null;

  const params = activeCall.parameters as Record<string, string>;
  const from = params.From ?? params.from ?? 'caller';

  function toggleMute() {
    if (!activeCall) return;
    const next = !muted;
    activeCall.mute(next);
    setMuted(next);
  }

  function endCall() {
    if (!activeCall) return;
    activeCall.disconnect();
  }

  async function pickInput(id: string) {
    if (!device?.audio) return;
    try {
      await device.audio.setInputDevice(id);
      setInputDeviceId(id);
    } catch (err) {
      setActionError(toMessage(err, 'failed to set microphone'));
    }
  }

  async function pickOutput(id: string) {
    if (!device?.audio) return;
    try {
      await device.audio.speakerDevices.set([id]);
      setOutputDeviceId(id);
    } catch (err) {
      setActionError(toMessage(err, 'failed to set speaker'));
    }
  }

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <div>
            <p className="text-sm font-semibold text-emerald-900">On call with {formatNumber(from)}</p>
            <p className="text-xs text-emerald-700 font-mono">{formatDuration(elapsed)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMute}
            className={`text-xs px-3 py-1.5 rounded-md font-medium ${
              muted ? 'bg-amber-200 text-amber-900' : 'bg-white text-slate-700 border border-slate-300'
            }`}
          >
            {muted ? 'Unmute' : 'Mute'}
          </button>
          <button
            onClick={() => setShowTransfer(v => !v)}
            className="text-xs px-3 py-1.5 rounded-md font-medium bg-white text-slate-700 border border-slate-300"
          >
            Transfer
          </button>
          <button
            onClick={endCall}
            className="text-xs px-3 py-1.5 rounded-md font-medium bg-red-600 text-white hover:bg-red-700"
          >
            End call
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Volume">
          <input
            type="range" min={0} max={100} value={Math.round(volume * 100)}
            onChange={e => setVolume(Number(e.target.value) / 100)}
            className="w-full"
          />
        </Field>
        <Field label="Microphone">
          <DeviceSelect
            map={device.audio?.availableInputDevices}
            value={inputDeviceId}
            onChange={pickInput}
          />
        </Field>
        <Field label="Speaker">
          <DeviceSelect
            map={device.audio?.availableOutputDevices}
            value={outputDeviceId}
            onChange={pickOutput}
          />
        </Field>
      </div>

      {showTransfer && (
        <TransferTargets
          onClose={() => setShowTransfer(false)}
          onTransferred={() => {
            setActive(undefined);
            setShowTransfer(false);
          }}
        />
      )}

      {actionError && <p className="text-xs text-red-700">{actionError}</p>}
    </div>
  );
}

function TransferTargets({
  onClose, onTransferred,
}: { onClose: () => void; onTransferred: () => void }) {
  const me = useAuth(s => s.user);

  // Fetch the live queue to find OUR active call's TaskSid (the Voice SDK
  // call object doesn't carry it through the dequeue dial).
  // Find OUR row in the queue so we can grab its callSid — that's what
  // the backend uses to look up direction and decide which Twilio call
  // leg to redirect (D-031 for inbound, D-036's child-call lookup for
  // outbound).
  const queueQuery = useQuery({
    queryKey: ['queue-for-transfer'],
    queryFn: () => api.getQueue(),
    refetchInterval: 3_000,
  });
  const myCallSid = queueQuery.data?.active.find(c => c.agentUserId === me?.userId)?.callSid;

  const targetsQuery = useQuery({
    queryKey: ['transfer-targets'],
    queryFn: () => api.transferTargets(),
  });
  const targets = targetsQuery.data?.targets ?? [];

  const transfer = useMutation({
    mutationFn: (targetUserId: string) => {
      if (!myCallSid) throw new Error('no active call');
      return api.transferCall(myCallSid, targetUserId);
    },
    onSuccess: () => onTransferred(),
  });

  const errorMsg = transfer.error instanceof ApiError
    ? (transfer.error.body as { error?: string } | undefined)?.error ?? `error ${transfer.error.status}`
    : transfer.error instanceof Error ? transfer.error.message : undefined;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-900">Transfer to…</h4>
        <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-800">Cancel</button>
      </div>
      {(targetsQuery.isLoading || queueQuery.isLoading) && (
        <p className="text-xs text-slate-500">Loading…</p>
      )}
      {targets.length === 0 && !targetsQuery.isLoading && (
        <p className="text-xs text-slate-500">No Available agents to transfer to.</p>
      )}
      <ul className="space-y-1.5">
        {targets.map(t => (
          <li key={t.userId} className="flex items-center gap-2 text-sm">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            <span className="flex-1 truncate" title={t.email}>{displayName(t)}</span>
            <button
              onClick={() => transfer.mutate(t.userId)}
              disabled={transfer.isPending || !myCallSid}
              className="text-xs px-2 py-1 rounded bg-brand text-white hover:bg-brand-dark disabled:opacity-50"
            >
              {transfer.isPending ? '…' : 'Transfer'}
            </button>
          </li>
        ))}
      </ul>
      {!myCallSid && !queueQuery.isLoading && (
        <p className="mt-2 text-xs text-amber-700">Transfer not available yet — waiting for queue sync.</p>
      )}
      {errorMsg && <p className="mt-2 text-xs text-red-700">{errorMsg}</p>}
    </div>
  );
}

function DeviceSelect({
  map, value, onChange,
}: { map: ReadonlyMap<string, MediaDeviceInfo> | undefined; value: string | undefined; onChange: (id: string) => void }) {
  const items = map ? Array.from(map.values()) : [];
  return (
    <select
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      disabled={items.length === 0}
      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs bg-white"
    >
      {items.length === 0 && <option>No devices found</option>}
      {items.map(d => (
        <option key={d.deviceId} value={d.deviceId}>
          {d.label || d.deviceId.slice(0, 8)}
        </option>
      ))}
    </select>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function formatNumber(raw: string): string {
  if (raw.startsWith('+61') && raw.length === 12) {
    return `${raw.slice(0, 3)} ${raw.slice(3, 6)} ${raw.slice(6, 9)} ${raw.slice(9)}`;
  }
  if (raw.startsWith('+1') && raw.length === 12) {
    return `${raw.slice(0, 2)} (${raw.slice(2, 5)}) ${raw.slice(5, 8)}-${raw.slice(8)}`;
  }
  return raw;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function toMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  return fallback;
}
